<template>
  <div class="page-wrapper">
    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Attendance Import</h1>
        <p class="page-subtitle">
          Upload bulk CSV attendance records for student tracking
        </p>
      </div>
      <div class="header-right">
        <v-btn
          color="#16a34a"
          :disabled="btnLoading"
          :loading="btnLoading"
          class="add-btn"
          elevation="0"
          @click="save()"
        >
          <v-icon start size="18">mdi-upload</v-icon>
          SAVE RECORDS
        </v-btn>
      </div>
    </div>

    <!-- ── Stat Cards ── -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-icon-wrap green">
          <v-icon size="20" color="#16a34a">mdi-calendar-check</v-icon>
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ csv_results.length }}</span>
          <span class="stat-label">Rows Loaded</span>
        </div>
      </div>
      <!-- <div class="stat-card">
        <div class="stat-icon-wrap blue">
          <v-icon size="20" color="#2563eb">mdi-account-school</v-icon>
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ studentList.length }}</span>
          <span class="stat-label">Students in Batch</span>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon-wrap amber">
          <v-icon size="20" color="#d97706">mdi-domain</v-icon>
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ orgList.length }}</span>
          <span class="stat-label">Organizations</span>
        </div>
      </div> -->
    </div>

    <!-- ── Configuration Card ── -->
    <div class="content-card">
      <div class="card-toolbar">
        <div class="toolbar-left">
          <span class="card-title">Upload Configuration</span>
        </div>
        <div class="toolbar-right">
          <div class="status-badge" :class="parsed ? 'ready' : 'idle'">
            <span class="badge-dot"></span>
            {{ parsed ? "CSV Ready" : "Awaiting File" }}
          </div>
        </div>
      </div>
      <v-divider></v-divider>

      <div class="fields-grid">
        <div class="field-wrap">
          <label class="field-lbl">Batch</label>
          <v-autocomplete
            v-model="batch_selected"
            @update:model-value="handleStudentOnChange"
            :items="batchList"
            item-value="id"
            :item-title="(item) => `${item.name}  (${item.year})`"
            placeholder="Select a batch"
            variant="outlined"
            density="compact"
            hide-details
            class="field-input"
            color="#16a34a"
          ></v-autocomplete>
        </div>

        <div class="field-wrap">
          <label class="field-lbl">CSV File</label>
          <v-file-input
            @change="parseCSV()"
            clearable
            placeholder="Upload .csv file"
            accept=".csv"
            variant="outlined"
            density="compact"
            v-model="csv_file"
            hide-details
            class="field-input"
            prepend-icon=""
            prepend-inner-icon="mdi-file-delimited-outline"
          ></v-file-input>
        </div>

        <div class="field-wrap">
          <label class="field-lbl">Student</label>
          <v-autocomplete
            v-model="student_id"
            :items="studentList"
            item-value="id"
            item-title="name"
            placeholder="Select student"
            variant="outlined"
            density="compact"
            class="field-input"
            hide-details
            color="#16a34a"
          ></v-autocomplete>
        </div>

        <div class="field-wrap">
          <label class="field-lbl">Organization</label>
          <v-autocomplete
            v-model="organization_id"
            :items="orgList"
            item-value="id"
            item-title="name"
            placeholder="Select organization"
            variant="outlined"
            density="compact"
            class="field-input"
            hide-details
            color="#16a34a"
          ></v-autocomplete>
        </div>
      </div>
    </div>

    <!-- ── Records Preview Card ── -->
    <div class="content-card mt-4">
      <div class="card-toolbar">
        <div class="toolbar-left">
          <span class="card-title">Records Preview</span>
          <span class="count-chip" v-if="csv_results.length > 0">{{
            csv_results.length
          }}</span>
        </div>
      </div>
      <v-divider></v-divider>

      <div class="loading-state" v-if="!parsed && csv_file">
        <v-progress-circular
          indeterminate
          color="#16a34a"
          size="26"
          width="2"
        ></v-progress-circular>
        <span>Parsing CSV file...</span>
      </div>

      <div class="empty-state" v-if="!csv_file">
        <v-icon size="40" color="#d1d5db">mdi-file-upload-outline</v-icon>
        <p>No file uploaded yet. Select a CSV file to preview records.</p>
      </div>

      <v-data-table
        v-if="parsed || csv_results.length > 0"
        :headers="headers"
        :items="csv_results"
        :loading="loading"
        hover
        :items-per-page="10"
        density="comfortable"
        class="records-table"
      >
        <template v-slot:[`item.date`]="{ item }">
          <span class="cell-date">{{ formatDate(item.date) }}</span>
        </template>
        <template v-slot:[`item.am_in`]="{ item }">
          <span v-if="item.am_in" class="time-chip am-in">{{
            item.am_in
          }}</span>
          <span v-else class="time-dash">—</span>
        </template>
        <template v-slot:[`item.am_out`]="{ item }">
          <span v-if="item.am_out" class="time-chip am-out">{{
            item.am_out
          }}</span>
          <span v-else class="time-dash">—</span>
        </template>
        <template v-slot:[`item.pm_in`]="{ item }">
          <span v-if="item.pm_in" class="time-chip pm-in">{{
            item.pm_in
          }}</span>
          <span v-else class="time-dash">—</span>
        </template>
        <template v-slot:[`item.pm_out`]="{ item }">
          <span v-if="item.pm_out" class="time-chip pm-out">{{
            item.pm_out
          }}</span>
          <span v-else class="time-dash">—</span>
        </template>
        <template v-slot:[`item.ot_in`]="{ item }">
          <span v-if="item.ot_in" class="time-chip pm-out">{{
            item.ot_in
          }}</span>
          <span v-else class="time-dash">—</span>
        </template>
        <template v-slot:[`item.ot_out`]="{ item }">
          <span v-if="item.ot_out" class="time-chip pm-out">{{
            item.ot_out
          }}</span>
          <span v-else class="time-dash">—</span>
        </template>
      </v-data-table>
    </div>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import Papa from "papaparse";
export default {
  props: { data: Object, action: String },
  data() {
    return {
      exceldata: [],
      fetched_data: [],
      btnLoading: false,
      batch_selected: null,
      batchList: [],
      student_id: null,
      organization_id: null,
      loading: false,
      csv_results: [],
      csv_file: null,
      parsed: false,
      headers: [
        { title: "Date", value: "date", align: "start" },
        { title: "AM IN", value: "am_in", align: "start" },
        { title: "AM OUT", value: "am_out", align: "start" },
        { title: "PM IN", value: "pm_in", align: "start" },
        { title: "PM OUT", value: "pm_out", align: "start" },
        { title: "OT IN", value: "ot_in", align: "start" },
        { title: "OT OUT", value: "ot_out", align: "start" },
      ],
      orgList: [],
      studentList: [],
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
    };
  },
  mounted() {
    this.getBatchList();
  },
  methods: {
    initialize() {},
    handleStudentOnChange() {
      this.getStudent();
      this.getOrganization();
    },
    parseCSV() {
      Papa.parse(this.csv_file, {
        header: true,
        skipEmptyLines: true,
        complete: function (results) {
          console.log(results.data);
          this.csv_results = results.data;
          this.parsed = true;
        }.bind(this),
      });
    },
    formatAttendanceToDatetime(records, studentId, organizationId) {
      const results = [];
      const typeKeys = [
        { key: "am_in", type: "am_in" },
        { key: "am_out", type: "am_out" },
        { key: "pm_in", type: "pm_in" },
        { key: "pm_out", type: "pm_out" },
        { key: "ot_in", type: "ot_in" },
        { key: "ot_out", type: "ot_out" },
      ];
      records.forEach((record) => {
        const { date } = record;
        typeKeys.forEach(({ key, type }) => {
          const timeStr = record[key];
          if (!timeStr || timeStr.trim() === "") return;
          const datetime = this.convertTo24h(date, timeStr);
          if (!datetime) return;
          results.push({
            student_id: studentId,
            organization_id: organizationId,
            datetime,
            type,
          });
        });
      });
      return results;
    },
    convertTo24h(date, timeStr) {
      const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return null;
      let hours = parseInt(match[1]);
      const mins = match[2];
      const period = match[3].toUpperCase();
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      return `${date} ${String(hours).padStart(2, "0")}:${mins}:00`;
    },
    async save() {
      this.btnLoading = true;
      if (
        this.student_id &&
        this.organization_id &&
        this.csv_results.length > 0
      ) {
        let json_arr = this.formatAttendanceToDatetime(
          this.csv_results,
          this.student_id,
          this.organization_id,
        );
        this.axiosCall("/attendance/bulk-create", "POST", {
          data: JSON.stringify(json_arr),
        }).then((res) => {
          if (res.data.status == 201) {
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.show = true;
            this.btnLoading = false;
            this.csv_results = [];
            this.student_id = null;
            this.organization_id = null;
            this.batch_selected = null;
            this.parsed = false;
            this.csv_file = null;
          } else if (res.data.status == 400) {
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.show = true;
            this.btnLoading = false;
          }
        });
      } else {
        this.btnLoading = false;
      }
    },
    getStudent() {
      // getAllStudentsListByBatch
      this.axiosCall(
        "/student/getAllStudentsListByBatch/" + this.batch_selected,
        "GET",
      ).then((res) => {
        let data = res.data;
        data.forEach((el, i) => {
          data[i].name = this.toTitleCase(el.name);
        });
        this.studentList = data;
      });
    },
    getOrganization() {
      this.axiosCall("/organization/tiedBy/" + this.batch_selected, "GET").then(
        (res) => {
          this.orgList = res.data;
        },
      );
    },
    getBatchList() {
      this.axiosCall("/student/get-batch-list", "GET").then((res) => {
        this.batchList = res.data;
      });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

.page-wrapper {
  font-family: "Plus Jakarta Sans", sans-serif;

  min-height: 100vh;
  padding: 32px 28px;
  color: #111827;
}

/* ── Header ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.page-title {
  font-size: 1.55rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 3px;
  letter-spacing: -0.02em;
}
.page-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.add-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  border-radius: 8px !important;
  padding: 0 20px !important;
  height: 40px !important;
  color: #fff !important;
}

/* ── Stats ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.stat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.stat-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-icon-wrap.green {
  background: #dcfce7;
}
.stat-icon-wrap.blue {
  background: #dbeafe;
}
.stat-icon-wrap.amber {
  background: #fef3c7;
}
.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  display: block;
}
.stat-label {
  font-size: 12px;
  color: #6b7280;
  display: block;
  margin-top: 2px;
}

/* ── Content Card ── */
.content-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
}
.mt-4 {
  margin-top: 16px;
}

.card-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.count-chip {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  padding: 1px 9px;
}

/* Status badge */
.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 20px;
}
.status-badge.idle {
  background: #f3f4f6;
  color: #9ca3af;
  border: 1px solid #e5e7eb;
}
.status-badge.ready {
  background: #dcfce7;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}
.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.status-badge.ready .badge-dot {
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.2);
}

/* ── Fields ── */
.fields-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  padding: 20px;
}
.field-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-lbl {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.field-input :deep(.v-field) {
  border-radius: 8px !important;
  background: #fff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}
.field-input :deep(.v-field__outline__start),
.field-input :deep(.v-field__outline__end),
.field-input :deep(.v-field__outline__notch) {
  border-color: #d1d5db !important;
}
.field-input :deep(.v-field--focused .v-field__outline__start),
.field-input :deep(.v-field--focused .v-field__outline__end),
.field-input :deep(.v-field--focused .v-field__outline__notch) {
  border-color: #16a34a !important;
}
.field-input :deep(input),
.field-input :deep(.v-field__input) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #111827 !important;
}
.field-input :deep(.v-label) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #9ca3af !important;
}

/* ── States ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 20px;
  gap: 10px;
}
.empty-state p {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 20px;
  font-size: 13px;
  color: #6b7280;
}

/* ── Table ── */
.records-table :deep(.v-data-table__th) {
  background: #f9fafb !important;
  color: #6b7280 !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #f3f4f6 !important;
  padding: 11px 20px !important;
}
.records-table :deep(td) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #374151 !important;
  padding: 11px 20px !important;
  border-bottom: 1px solid #f9fafb !important;
}
.records-table :deep(.v-data-table__tr:hover > td) {
  background: #f9fafb !important;
}

.cell-date {
  font-weight: 600;
  color: #111827;
}

.time-chip {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 6px;
}
.time-chip.am-in {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}
.time-chip.am-out {
  background: #d1fae5;
  color: #059669;
  border: 1px solid #a7f3d0;
}
.time-chip.pm-in {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.time-chip.pm-out {
  background: #ede9fe;
  color: #6d28d9;
  border: 1px solid #ddd6fe;
}
.time-dash {
  color: #d1d5db;
  font-size: 15px;
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: 1fr;
  }
  .fields-grid {
    grid-template-columns: 1fr;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
}
</style>
